import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Contexts
import { OrganizationProvider } from './contexts/OrganizationContext';
import { AdminAuthProvider } from './admin/contexts/AdminAuthContext';

// Error Boundary
import ErrorBoundary from './components/ErrorBoundary';

// Public Components (eager load - needed immediately)
import MultiPageTaxForm from './MultiPageTaxForm';
import OrganizationDebug from './components/OrganizationDebug';

// Admin Components (lazy load - code splitting)
const AdminLogin = lazy(() => import('./admin/pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard'));
const SubmissionDetail = lazy(() => import('./admin/pages/SubmissionDetail'));
const Submissions = lazy(() => import('./admin/pages/Submissions'));
const Settings = lazy(() => import('./admin/pages/Settings'));
const PrepFees = lazy(() => import('./admin/pages/PrepFees'));
const ProtectedAdminRoute = lazy(() => import('./admin/components/ProtectedAdminRoute'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

/**
 * ENTERPRISE APP ARCHITECTURE
 * 
 * Key Design Principles:
 * 1. Error Boundary - Catches errors and prevents app crashes
 * 2. Single BrowserRouter - prevents context remounting on navigation
 * 3. Context Providers above Routes - auth/org state persists across navigation
 * 4. Lazy Loading - code splitting for optimal performance
 * 5. Multi-Tenant Isolation - org-scoped data, domain-based routing
 * 
 * Route Hierarchy:
 * - /admin/* → Admin portal (AdminAuthProvider wraps these routes)
 * - /* → Public tax form (OrganizationProvider wraps these routes)
 */
function App() {
  // Dynamic Favicon - Load based on domain
  useEffect(() => {
    const loadDynamicFavicon = async () => {
      const hostname = window.location.hostname;
      console.log('🌐 Loading favicon for domain:', hostname);
      
      // Determine which domain we're on
      let faviconUrl = '';
      let title = '';
      
      if (hostname.includes('zeabtaxform.com') || hostname.includes('zeab')) {
        // Zeab LLC
        faviconUrl = 'https://dngfoiurhphjpsvaeuut.supabase.co/storage/v1/object/public/logos/zeab-favicon.png.png';
        title = 'Zeab LLC - Tax Form 2025';
        console.log('✅ Loading Zeab favicon');
      } else {
        // Gator Agent LLC (default)
        faviconUrl = 'https://dngfoiurhphjpsvaeuut.supabase.co/storage/v1/object/public/logos/gator-favicon.png.webp';
        title = 'Gator Agent LLC - Tax Form 2025';
        console.log('✅ Loading Gator Agent favicon');
      }
      
      // Update favicon
      const favicon = document.querySelector("link[rel*='icon']") || document.createElement('link');
      favicon.type = 'image/png';
      favicon.rel = 'icon';
      favicon.href = faviconUrl;
      if (!document.querySelector("link[rel*='icon']")) {
        document.head.appendChild(favicon);
      }
      
      // Update title
      document.title = title;
      
      // Update apple-touch-icon
      const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") || document.createElement('link');
      appleTouchIcon.rel = 'apple-touch-icon';
      appleTouchIcon.href = faviconUrl;
      if (!document.querySelector("link[rel='apple-touch-icon']")) {
        document.head.appendChild(appleTouchIcon);
      }
      
      console.log('🎨 Favicon and title updated!');
    };
    
    loadDynamicFavicon();
  }, []); // Run once on mount

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          {/* ============================================
              ADMIN ROUTES - Multi-Tenant Admin Portal
              ============================================ */}
          <Route path="/admin/*" element={
            <AdminAuthProvider>
              <Routes>
                {/* Public admin route - login */}
                <Route path="login" element={<AdminLogin />} />

                {/* Protected admin routes */}
                <Route element={<ProtectedAdminRoute />}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="submissions" element={<Submissions />} />
                  <Route path="submissions/:id" element={<SubmissionDetail />} />
                  <Route path="fees" element={<PrepFees />} />
                  <Route path="settings" element={<Settings />} />
                </Route>

                {/* Catch-all for admin - redirect to dashboard or login */}
                <Route path="*" element={<Navigate to="/admin/login" replace />} />
              </Routes>
            </AdminAuthProvider>
          } />

          {/* ============================================
              PUBLIC ROUTES - Multi-Tenant Tax Form
              ============================================ */}
          <Route path="/*" element={
            <OrganizationProvider>
              <div className="App">
                <MultiPageTaxForm />
                <OrganizationDebug />
              </div>
            </OrganizationProvider>
          } />
        </Routes>
      </Suspense>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;